import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "hooks/useSession";
import { exportDataToFile } from "utils/exportUtils";

/*
  Standalone commission table — deliberately does NOT use the shared
  DataTable / useTableData / tableConfigs components. It fetches, formats,
  and renders com_data entirely on its own, so it can't break from
  assumptions baked into those shared components.

  API response shape handled:
  {
    "com_data": [ { name, start_slab, end_slab, type, commission }, ... ],
    "data": [ ... ],   // ignored here on purpose
    "status": true
  }
*/

const API_URL = "https://pro.mokshiq.in/retailer_commission";

const formatSlab = (row) => {
  const start = row.start_slab ?? 0;
  const end = row.end_slab ?? 0;
  if (start === 0 && end === 0) return "—";
  return `₹${start} – ₹${end}`;
};

const formatCommission = (row) => {
  const val = row.commission ?? "0";
  return row.type === "percent" ? `${val}%` : `₹${val}`;
};

const UserOtherCommissionManager = () => {
  const { userid, role } = useSession();
  const roleId = Number(localStorage.getItem("role_id") || 0);
  const currentRole = (role || localStorage.getItem("role") || "").trim().toLowerCase();
  const isAPIPartner =
    currentRole === "api partner" ||
    currentRole === "apipartner" ||
    currentRole === "api_partner" ||
    roleId === 9;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);

  const fetchComData = useCallback(async () => {
    if (!userid) return;
    setLoading(true);
    setError(null);
    const headers = {};
    const token = localStorage.getItem("authToken");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    try {
      const res = await fetch(`${API_URL}?id=${encodeURIComponent(userid)}`, { headers });
      const json = await res.json();
      if (json?.status) {
        setRows(Array.isArray(json.com_data) ? json.com_data : []);
      } else {
        setError(json?.message || "Failed to load commission data");
        setRows([]);
      }
    } catch (err) {
      console.error("retailer_commission fetch failed", err);
      setError("Network error while loading commission data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userid]);

  useEffect(() => {
    fetchComData();
  }, [fetchComData, refresh]);

  const filteredRows = rows
    .filter((r) => {
      if (!isAPIPartner) return true;
      const name = (r.name || "").toLowerCase();
      return name.includes("settlement") || name.includes("payout") || name.includes("wallet");
    })
    .filter((r) =>
      (r.name || "").toLowerCase().includes(search.toLowerCase())
    );

  const handleExport = (format = "xlsx") => {
    if (filteredRows.length === 0) return;
    const exportData = filteredRows.map((r) => ({
      "Service Name": r.name || "",
      "Start Slab (₹)": r.start_slab ?? 0,
      "End Slab (₹)": r.end_slab ?? 0,
      "Slab Range": formatSlab(r),
      "Calculation Type": r.type || "flat",
      "Commission / Fee": formatCommission(r),
    }));

    exportDataToFile(
      exportData,
      isAPIPartner ? "wallet_settlement_slabs" : "other_commission_slabs",
      format
    );
  };

  const pageTitle = isAPIPartner ? "Wallet Settlement Slabs" : "Other Service Commission";

  return (
    <main className="app-main">
      <div className="container-fluid py-4 px-4">
        {/* Breadcrumb & Header */}
        <div className="row mb-3">
          <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="main-title mb-1">{pageTitle}</h4>
              <ul className="app-line-breadcrumbs mb-0">
                <li>
                  <a className="f-s-14 f-w-500" href="#">
                    <span className="d-flex align-items-center gap-1">
                      <i className="ph-duotone ph-user-circle f-s-16"></i> Profile Manager
                    </span>
                  </a>
                </li>
                <li className="active">
                  <span className="f-s-14 f-w-500">{pageTitle}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="card border-0 shadow-sm rounded-3">
          <div className="card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-3 p-4 border-bottom">
            <div>
              <h5 className="mb-1 fw-bold text-dark">{pageTitle}</h5>
              <p className="text-secondary f-s-13 mb-0">
                {isAPIPartner
                  ? "Configured payout & settlement slabs and commission charges for your account."
                  : "Configured commission rates and slabs across other platform services."}
              </p>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ width: 240, paddingLeft: 32 }}
                  placeholder="Search service name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    fontSize: "12px",
                  }}
                >
                  🔍
                </span>
              </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                onClick={() => setRefresh((p) => p + 1)}
                disabled={loading}
                title="Refresh commission records"
              >
                <i className="ph-bold ph-arrows-clockwise"></i> Refresh
              </button>

              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExport("xlsx")}
                  disabled={filteredRows.length === 0}
                  title="Export as Excel spreadsheet"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className="ph-bold ph-file-xls"></i> Excel
                </button>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExport("csv")}
                  disabled={filteredRows.length === 0}
                  title="Export as CSV file"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <i className="ph-bold ph-file-csv"></i> CSV
                </button>
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3 text-muted mb-0 f-s-14">Loading settlement data from server...</p>
              </div>
            ) : error ? (
              <div className="text-center py-5">
                <p className="text-danger mb-2">{error}</p>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setRefresh((p) => p + 1)}
                >
                  Try Again
                </button>
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="text-center py-5 text-muted f-s-14">
                No settlement or commission records found{search ? ` for "${search}"` : ""}.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                  <thead style={{ background: "#f8fafc" }}>
                    <tr>
                      <th style={{ width: 60, padding: "14px 18px", color: "#64748b", fontWeight: 700 }}>#</th>
                      <th style={{ padding: "14px 18px", color: "#64748b", fontWeight: 700 }}>Service Name</th>
                      <th style={{ padding: "14px 18px", color: "#64748b", fontWeight: 700 }}>Slab Range</th>
                      <th style={{ padding: "14px 18px", color: "#64748b", fontWeight: 700 }}>Type</th>
                      <th className="text-end" style={{ padding: "14px 18px", color: "#64748b", fontWeight: 700 }}>
                        Commission / Charge
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row, idx) => (
                      <tr key={`${row.name}-${row.start_slab}-${row.end_slab}-${idx}`}>
                        <td style={{ padding: "14px 18px", color: "#94a3b8" }}>{idx + 1}</td>
                        <td style={{ padding: "14px 18px", fontWeight: 600, color: "#1e293b" }}>{row.name}</td>
                        <td style={{ padding: "14px 18px", fontFamily: "'JetBrains Mono', monospace", color: "#475569" }}>
                          {formatSlab(row)}
                        </td>
                        <td style={{ padding: "14px 18px" }}>
                          <span
                            className={`badge px-2 py-1 ${
                              row.type === "percent" ? "bg-info-subtle text-info" : "bg-secondary-subtle text-secondary"
                            }`}
                            style={{ fontSize: "11px", fontWeight: 600 }}
                          >
                            {row.type}
                          </span>
                        </td>
                        <td
                          className="text-end"
                          style={{
                            padding: "14px 18px",
                            fontWeight: 700,
                            fontFamily: "'JetBrains Mono', monospace",
                            color: "#059669",
                          }}
                        >
                          {formatCommission(row)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!loading && !error && filteredRows.length > 0 && (
            <div className="card-footer bg-white text-muted f-s-13 py-3 px-4 border-top">
              Showing {filteredRows.length} of {rows.length} record{rows.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default UserOtherCommissionManager;
